import axios from 'axios';
import { useEffect } from 'react';
import { Appointment } from '../types/booking';
import { formatAppointmentResponse } from '../utils/format';

export type StoredAppointment = {
	businessId: string;
	serviceId: string;
	appointmentId: string;
};

type UseAppointmentStorageProps = {
	bookingBusinessId: string;
	bookingServiceId: string;
	getAppointmentUrl: string;
	setAppointment: (appointment: Appointment | null) => void;
	setAppointmentSource: (source: 'stored' | 'created' | null) => void;
};

export const useAppointmentStorage = ({
	bookingBusinessId,
	bookingServiceId,
	getAppointmentUrl,
	setAppointment,
	setAppointmentSource,
}: UseAppointmentStorageProps) => {
	const STORAGE_KEY = 'bookingAppointments';
	const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

	const readAppointments = (): StoredAppointment[] => {
		if (!isBrowser) return [];
		try {
			const storageValue = localStorage.getItem(STORAGE_KEY);
			if (!storageValue) return [];
			const parsed = JSON.parse(storageValue);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	};

	const writeAppointments = (appointments: StoredAppointment[]) => {
		if (!isBrowser) return;
		try {
			if (!appointments.length) {
				localStorage.removeItem(STORAGE_KEY);
			} else {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(appointments));
			}
		} catch (error) {
			console.error('Failed to write appointments to localStorage', error);
		}
	};

	const removeStoredAppointment = (appointmentId: string) => {
		try {
			const next = readAppointments().filter((apt) => apt.appointmentId !== appointmentId);
			writeAppointments(next);
		} catch (error) {
			console.error('Failed to remove appointment from localStorage', error);
		}
	};

	const getAppointmentData = async (businessId: string, appointmentId: string) => {
		try {
			const response = await axios.post(getAppointmentUrl, {
				booking_business_id: businessId,
				booking_appointment_id: appointmentId,
			});
			return formatAppointmentResponse(response.data);
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				const status = error.response?.status;
				if (status === 404 || status === 410) {
					removeStoredAppointment(appointmentId);
					console.warn('Appointment not found', { businessId, appointmentId, status });
					return null;
				}
			}
			console.error('Failed to get appointment data', error);
			throw error;
		}
	};

	const getStoredAppointmentId = (): null | string => {
		const appointments = readAppointments();
		if (!appointments.length) return null;

		// Find appointment matching both businessId and serviceId
		const firstMatchingAppointment = appointments.find(
			({ businessId, serviceId }: StoredAppointment) => businessId === bookingBusinessId && serviceId === bookingServiceId,
		);

		if (!firstMatchingAppointment) {
			return null;
		}

		return firstMatchingAppointment.appointmentId;
	};

	const storeAppointment = (businessId: string, serviceId: string, appointmentId: string) => {
		try {
			let appointments = readAppointments();

			const newAppointment: StoredAppointment = { businessId, serviceId, appointmentId };

			// Remove any existing appointment with the same businessId and serviceId
			appointments = appointments.filter((apt) => !(apt.businessId === businessId && apt.serviceId === serviceId));

			// Add the new appointment
			appointments.push(newAppointment);

			writeAppointments(appointments);
		} catch (error) {
			console.error('Failed to store appointment in localStorage', error);
		}
	};

	useEffect(() => {
		let cancelled = false;
		const storedAppointmentId = getStoredAppointmentId();
		if (!storedAppointmentId) return;

		getAppointmentData(bookingBusinessId, storedAppointmentId)
			.then((data) => {
				if (cancelled) return;
				if (!data) return;
				setAppointment(data);
				setAppointmentSource('stored');
			})
			.catch((error) => {
				if (cancelled) return;
				console.error('Could not retrieve stored appointment', error);
			});

		return () => {
			cancelled = true;
		};
	}, [bookingBusinessId, bookingServiceId]);

	return { removeStoredAppointment, storeAppointment };
};
